/**
 * Utility functions for handling modal behavior consistently across the application
 */

/**
 * Creates a keyboard event to simulate pressing the Escape key
 * @returns A keyboard event for the Escape key
 */
const createEscapeKeyEvent = (): KeyboardEvent => {
  return new KeyboardEvent('keydown', {
    key: 'Escape',
    code: 'Escape',
    keyCode: 27,
    which: 27,
    bubbles: true,
    cancelable: true
  });
};

/**
 * Applies click-outside behavior to all modal elements in the DOM
 * This function should be called after the application has loaded
 * to ensure all modals have consistent behavior for legacy components
 * that don't use the useModalBehavior hook
 */
export const applyClickOutsideBehaviorToAllModals = (): void => {
  // Function to handle click events on modal backdrops
  const handleBackdropClick = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;

    // Check if the clicked element is a backdrop
    if (
      target.classList.contains('MuiBackdrop-root') ||
      target.classList.contains('MuiModal-backdrop')
    ) {
      // Find the closest dialog, popover, or menu element
      const modalElement = target.closest('.MuiDialog-root, .MuiPopover-root, .MuiMenu-root, .MuiDrawer-root');

      if (modalElement) {
        // Skip if the modal has data-disable-backdrop-click attribute
        if (modalElement.getAttribute('data-disable-backdrop-click') === 'true') {
          return;
        }

        // Skip if any select dropdown is open within the modal
        // Check for open select dropdowns in multiple ways

        // Method 1: Check for aria-expanded attribute
        const selectDropdowns = modalElement.querySelectorAll('.MuiSelect-select');
        for (let i = 0; i < selectDropdowns.length; i++) {
          const select = selectDropdowns[i];
          const selectParent = select.closest('.MuiSelect-root');
          if (selectParent && selectParent.getAttribute('aria-expanded') === 'true') {
            return;
          }
        }

        // Method 2: Check for open popover/menu elements
        const openMenus = document.querySelectorAll('.MuiPopover-root[aria-hidden="false"], .MuiMenu-root[aria-hidden="false"]');
        if (openMenus.length > 0) {
          // If there's any open menu/popover, don't close the modal
          return;
        }

        // Find the close button and simulate a click
        const closeButton = modalElement.querySelector('[aria-label="close"]') as HTMLElement;
        if (closeButton) {
          closeButton.click();
          return;
        }

        // If no close button, dispatch an escape key event to close the modal
        modalElement.dispatchEvent(createEscapeKeyEvent());
      }
    }
  };

  // Fix for mobile menus
  const fixMobileMenus = () => {
    // Find all menu and popover backdrops
    const menuBackdrops = document.querySelectorAll('.MuiPopover-root .MuiBackdrop-root, .MuiMenu-root .MuiBackdrop-root');

    menuBackdrops.forEach(backdrop => {
      // Skip if already processed
      if ((backdrop as HTMLElement).dataset.clickHandlerAdded === 'true') {
        return;
      }

      // Mark as processed
      (backdrop as HTMLElement).dataset.clickHandlerAdded = 'true';

      // Add click event listener with passive option for better performance
      backdrop.addEventListener('click', (e) => {
        // Find the closest menu or popover element
        const menuElement = backdrop.closest('.MuiPopover-root, .MuiMenu-root');
        if (menuElement) {
          // Skip if the modal has data-disable-backdrop-click attribute
          if (menuElement.getAttribute('data-disable-backdrop-click') === 'true') {
            return;
          }

          // Skip if this is a Select dropdown
          if (menuElement.querySelector('.MuiSelect-select')) {
            return;
          }

          // Skip if any select dropdown is open within the modal
          // Method 1: Check for aria-expanded attribute
          const selectDropdowns = menuElement.querySelectorAll('.MuiSelect-select');
          for (let i = 0; i < selectDropdowns.length; i++) {
            const select = selectDropdowns[i];
            const selectParent = select.closest('.MuiSelect-root');
            if (selectParent && selectParent.getAttribute('aria-expanded') === 'true') {
              return;
            }
          }

          // Method 2: Check if this is part of a select dropdown
          const isPartOfSelect = menuElement.closest('.MuiPopover-root')?.querySelector('.MuiSelect-select');
          if (isPartOfSelect) {
            return;
          }

          // Find all close buttons in the menu
          const closeButtons = menuElement.querySelectorAll('[aria-label="close"]');
          if (closeButtons.length > 0) {
            // Click the first close button
            (closeButtons[0] as HTMLElement).click();
          } else {
            // If no close button, dispatch an escape key event
            menuElement.dispatchEvent(createEscapeKeyEvent());
          }
        }
      }, { passive: true });
    });
  };

  // Add global event listeners with passive option for better performance
  document.addEventListener('click', handleBackdropClick as EventListener, {
    capture: true,
    passive: true
  });

  // Add a global event listener for touch events (for mobile)
  document.addEventListener('touchend', handleBackdropClick as EventListener, {
    capture: true,
    passive: true
  });

  // Use MutationObserver to detect when new modals are added to the DOM
  const observer = new MutationObserver((mutations) => {
    let shouldFixMenus = false;

    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        // Check if any of the added nodes are modals
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const isModal =
              node.classList.contains('MuiPopover-root') ||
              node.classList.contains('MuiMenu-root') ||
              node.classList.contains('MuiDialog-root') ||
              node.classList.contains('MuiDrawer-root') ||
              node.querySelector('.MuiPopover-root, .MuiMenu-root, .MuiDialog-root, .MuiDrawer-root');

            if (isModal) {
              shouldFixMenus = true;
            }
          }
        });
      }
    });

    // Only run the fix if we found modal elements
    if (shouldFixMenus) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        fixMobileMenus();
      });
    }
  });

  // Start observing the document body for changes
  observer.observe(document.body, { childList: true, subtree: true });

  // Apply the fix immediately in case menus are already in the DOM
  // Use requestAnimationFrame for better performance
  requestAnimationFrame(fixMobileMenus);
};

/**
 * Cleans up any lingering modal elements in the DOM
 * This function should be called when components unmount
 * to ensure proper cleanup of modal elements
 *
 * @param immediate Whether to clean up immediately without waiting for transitions
 */
export const cleanupModalElements = (immediate: boolean = false): void => {
  const cleanupFn = () => {
    // Selectors for different types of modals
    const selectors = [
      // Backdrops
      '.MuiBackdrop-root[aria-hidden="true"]',
      // Drawers
      '.MuiDrawer-root.MuiDrawer-modal.MuiModal-root[aria-hidden="true"]',
      // Menus
      '.MuiMenu-root.MuiPopover-root.MuiModal-root[aria-hidden="true"]',
      // Dialogs
      '.MuiDialog-root.MuiModal-root[aria-hidden="true"]',
      // Popovers
      '.MuiPopover-root.MuiModal-root[aria-hidden="true"]'
    ];

    // Find all lingering modal elements
    const modalElements = document.querySelectorAll(selectors.join(', '));

    // Hide all lingering modal elements
    modalElements.forEach(element => {
      (element as HTMLElement).style.display = 'none';
    });

    // Also remove any body styles that might have been added by MUI
    const body = document.body;
    if (body.style.overflow === 'hidden') {
      body.style.overflow = '';
    }
    if (body.style.paddingRight) {
      body.style.paddingRight = '';
    }
  };

  // Either clean up immediately or wait for transitions to complete
  if (immediate) {
    cleanupFn();
  } else {
    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      // Wait for transitions to complete
      setTimeout(cleanupFn, 300);
    });
  }
};
